/**
 * Point Buy & Ability Score Generation — D&D 5e rules.
 */
/** Standard Point Buy cost table (score → total cost). */
export const POINT_BUY_COSTS = {
    8: 0,
    9: 1,
    10: 2,
    11: 3,
    12: 4,
    13: 5,
    14: 7,
    15: 9,
};
/** Total budget for Point Buy (D&D 5e standard). */
export const POINT_BUY_BUDGET = 27;
/** Minimum and maximum scores for Point Buy. */
export const POINT_BUY_MIN = 8;
export const POINT_BUY_MAX = 15;
/** Standard Array values. */
export const STANDARD_ARRAY = [15, 14, 13, 12, 10, 8];
export const ABILITY_KEYS = ['str', 'dex', 'con', 'int', 'wis', 'cha'];
/** Get Point Buy cost for a single score. Returns -1 for invalid scores. */
export function getPointBuyCost(score) {
    return POINT_BUY_COSTS[score] ?? -1;
}
/** Get total Point Buy cost for all six scores. Returns -1 if any score is invalid. */
export function getTotalCost(scores) {
    let total = 0;
    for (const key of ABILITY_KEYS) {
        const cost = getPointBuyCost(scores[key]);
        if (cost < 0)
            return -1;
        total += cost;
    }
    return total;
}
/** Get remaining Point Buy budget. Returns negative if over budget, -1 if invalid scores. */
export function getRemainingPoints(scores) {
    const total = getTotalCost(scores);
    if (total < 0)
        return -1;
    return POINT_BUY_BUDGET - total;
}
/** Validate a Point Buy allocation: all scores 8-15, total cost = 27. */
export function isValidPointBuy(scores) {
    for (const key of ABILITY_KEYS) {
        const v = scores[key];
        if (v < POINT_BUY_MIN || v > POINT_BUY_MAX)
            return false;
    }
    const total = getTotalCost(scores);
    return total === POINT_BUY_BUDGET;
}
/** Validate Standard Array assignment: each value used exactly once. */
export function isValidStandardArray(scores) {
    const used = [...STANDARD_ARRAY];
    for (const key of ABILITY_KEYS) {
        const idx = used.indexOf(scores[key]);
        if (idx < 0)
            return false;
        used.splice(idx, 1);
    }
    return used.length === 0;
}
/** Compute D&D 5e ability modifier from a score. */
export function abilityMod(score) {
    return Math.floor((score - 10) / 2);
}
